import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Snackbar,
  Switch,
  Typography,
} from '@mui/joy';
import { Paper, Typography as MuiTypography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import { getAllSettings, updateSettingById } from '../../../main/API/settings';
import { translateAxiosErrorMessage } from '../../../main/utils/masterApiError';

const FLAGS = [
  {
    name: 'CUSTOMER_UNIT_SHOW_SERIAL_NUMBER',
    fieldKey: 'serial_number',
    labelKey: 'customerUnitVisibility.serialNumber',
    fallback: 'Unit serial number',
  },
  {
    name: 'CUSTOMER_UNIT_SHOW_TRACKING_URL',
    fieldKey: 'tracking_url',
    labelKey: 'customerUnitVisibility.trackingUrl',
    fallback: 'Carrier tracking link',
  },
  {
    name: 'CUSTOMER_UNIT_SHOW_ORIGIN_WAREHOUSE',
    fieldKey: 'origin_warehouse',
    labelKey: 'customerUnitVisibility.originWarehouse',
    fallback: 'Origin warehouse',
  },
  {
    name: 'CUSTOMER_UNIT_SHOW_DESTINATION_WAREHOUSE',
    fieldKey: 'destination_warehouse',
    labelKey: 'customerUnitVisibility.destinationWarehouse',
    fallback: 'Destination warehouse',
  },
  {
    name: 'CUSTOMER_UNIT_SHOW_EXPECTED_DESTINATION_ETA',
    fieldKey: 'expected_destination_eta',
    labelKey: 'customerUnitVisibility.expectedDestinationEta',
    fallback: 'Expected destination ETA',
  },
  {
    name: 'CUSTOMER_UNIT_SHOW_EXPECTED_WAREHOUSE_ETA',
    fieldKey: 'expected_warehouse_eta',
    labelKey: 'customerUnitVisibility.expectedWarehouseEta',
    fallback: 'Expected warehouse ETA',
  },
];

const truthy = (val) => String(val ?? '').trim().toLowerCase() === 'true';

export default function CustomerShipmentUnitVisibilitySetting() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();

  const canList = hasPermission(PERMISSIONS.SETTINGS_LIST);
  const canUpdate = hasPermission(PERMISSIONS.SETTINGS_UPDATE_DETAIL);

  const [loading, setLoading] = useState(false);
  const [savingName, setSavingName] = useState(null);
  const [rows, setRows] = useState({});
  const [snack, setSnack] = useState({ open: false, message: '', color: 'success' });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!canList) return;
    setLoading(true);
    setError(null);
    try {
      const settings = await getAllSettings();
      const list = Array.isArray(settings) ? settings : [];
      const next = {};
      FLAGS.forEach(({ name }) => {
        next[name] = list.find((s) => s && s.name === name) || null;
      });
      setRows(next);
    } catch (e) {
      setError(
        translateAxiosErrorMessage(e, t)
          || t('customerUnitVisibility.loadFailed', { defaultValue: 'Failed to load customer unit visibility settings' })
      );
    } finally {
      setLoading(false);
    }
  }, [canList, t]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = useCallback(async (name, nextChecked) => {
    const setting = rows[name];
    if (!canUpdate || !setting) return;
    setSavingName(name);
    setError(null);
    const nextValue = nextChecked ? 'true' : 'false';
    try {
      await updateSettingById(setting.id, {
        name: setting.name,
        value: nextValue,
        description: setting.description || '',
      });
      setRows((prev) => ({ ...prev, [name]: { ...setting, value: nextValue } }));
      setSnack({
        open: true,
        color: 'success',
        message: t('customerUnitVisibility.saved', { defaultValue: 'Customer unit visibility updated.' }),
      });
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('siteSettingsUpdated'));
      }
    } catch (e) {
      setSnack({
        open: true,
        color: 'danger',
        message: translateAxiosErrorMessage(e, t)
          || t('customerUnitVisibility.saveFailed', { defaultValue: 'Could not save customer unit visibility setting.' }),
      });
    } finally {
      setSavingName(null);
    }
  }, [rows, canUpdate, t]);

  return (
    <PermissionGate permissions={[PERMISSIONS.SETTINGS_LIST]}>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#EEF2FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5,
                }}
              >
                <VisibilityOutlinedIcon sx={{ color: '#4F46E5', fontSize: 24 }} />
              </Box>
              <Box>
                <MuiTypography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                  {t('customerUnitVisibility.title', { defaultValue: 'Customer shipment unit fields' })}
                </MuiTypography>
                <MuiTypography variant="body2" color="#6B7280">
                  {t('customerUnitVisibility.subtitle', {
                    defaultValue: 'Choose which shipment unit details customers can see across tracking, lists, and exports.',
                  })}
                </MuiTypography>
              </Box>
            </Box>
            <Divider />
          </Box>

          <Alert color="neutral" variant="soft" sx={{ mb: 2 }}>
            {t('customerUnitVisibility.notice', {
              defaultValue: 'Fields are hidden by default. Turn ON each field you want customers to see.',
            })}
          </Alert>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size="sm" />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {FLAGS.map(({ name, labelKey, fallback }) => {
                const setting = rows[name];
                const checked = setting ? truthy(setting.value) : false;
                const busy = savingName === name;
                return (
                  <Grid item xs={12} md={6} key={name}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #F3F4F6',
                        background: '#FFFFFF',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          border: '1px solid #E5E7EB',
                          boxShadow: '0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -5px rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="title-sm" sx={{ fontWeight: 600, color: '#1F2937', mb: 0.25 }}>
                          {t(labelKey, { defaultValue: fallback })}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontFamily: 'monospace' }}>
                          {name}
                        </Typography>
                        {!setting && (
                          <Typography level="body-xs" sx={{ color: 'warning.700', mt: 0.5 }}>
                            {t('customerUnitVisibility.missingFlag', {
                              defaultValue: 'Setting "{{name}}" was not found. Run backend migrations to seed defaults.',
                              name,
                            })}
                          </Typography>
                        )}
                      </Box>
                      {busy ? (
                        <CircularProgress size="sm" />
                      ) : (
                        <Switch
                          checked={checked}
                          onChange={(e) => handleToggle(name, e.target.checked)}
                          disabled={!setting || !canUpdate}
                          color={checked ? 'success' : 'neutral'}
                        />
                      )}
                    </Paper>
                  </Grid>
                );
              })}
              {error && (
                <Grid item xs={12}>
                  <Alert color="danger" variant="soft">
                    {error}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
      </Paper>

      <Snackbar
        autoHideDuration={3000}
        open={snack.open}
        variant="soft"
        color={snack.color}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSnack((s) => ({ ...s, open: false })); }}
      >
        {snack.message}
      </Snackbar>
    </PermissionGate>
  );
}
